// src/features/bom/pages/BomPosPage.tsx
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { usePosStore, PaymentItem } from '@/store/posStore';
import { useToastStore } from '@/store/toastStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { CartPanel } from '@/features/pos/components/CartPanel';
import { PaymentModal } from '@/features/pos/components/PaymentModal';
import { CustomerModal } from '@/features/pos/components/CustomerModal';
import { ReceiptModal } from '@/features/pos/components/ReceiptModal';
import { ShiftModal } from '@/features/pos/components/ShiftModal';
import { ShiftCloseModal } from '@/features/pos/components/ShiftCloseModal';
import {
    parkCart, checkout, findCustomerByPhone,
    getActiveShift, openShift,
} from '@/features/pos/services/posApi';
import {
    getAllRecipes, getTodayBatches, decrementPortions,
    addPortions, startProduction,
    checkoutWithTimeout,
    parkCartWithTimeout,
    directCheckoutWithTimeout,
    decrementPortionsWithTimeout,
    BomRecipe,
} from '../services/bomApi';
import { getCategories } from '@/features/products/services/productsApi';
import { getMyPosPermissions, getBranches } from '@/features/settings/services/settingsApi';
import {
    ChefHat, Clock, Search, X, Coffee, Utensils, ArrowLeft,
    ShoppingCart, Receipt, Users, Flame, Timer, CheckCircle2,
    AlertCircle, SlidersHorizontal, Ban,
    MessageSquare, Table2, DoorOpen,
    Bike, ShoppingBag, Sparkles, Plus, Minus, Banknote, RefreshCw, Zap
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import confetti from 'canvas-confetti';
import apiClient from '@/lib/api-client';

// Types
interface MenuItem {
    recipeId: string;
    productId: string;
    name: string;
    categoryId: string;
    categoryName: string;
    description: string | null;
    retailPrice: string;
    yieldQuantity: string;
    yieldUomId: string;
    yieldUomName: string;
    preparationTime: string | null;
    ingredientCount: number;
    isAvailable: boolean;
    totalRemaining: number;
    hasActiveProduction: boolean;
    activeBatches: Array<{
        batchId: string;
        batchNumber: string;
        portionsRemaining: number;
        sellingPrice: string;
    }>;
}

interface CategoryGroup {
    id: string;
    name: string;
    icon?: string;
    items: MenuItem[];
}

// Food emoji mapper
function getFoodEmoji(name: string, categoryName?: string): string {
    const text = `${name} ${categoryName || ''}`.toLowerCase();
    if (text.includes('jollof') || text.includes('fried rice') || text.includes('ofada') || text.includes('rice')) return '🍚';
    if (text.includes('chicken') || text.includes('poultry') || text.includes('wings') || text.includes('drumstick')) return '🍗';
    if (text.includes('beef') || text.includes('steak') || text.includes('suya') || text.includes('meat') || text.includes('goat') || text.includes('asun')) return '🥩';
    if (text.includes('fish') || text.includes('tilapia') || text.includes('salmon') || text.includes('catfish') || text.includes('seafood') || text.includes('prawn')) return '🐟';
    if (text.includes('soup') || text.includes('stew') || text.includes('egusi') || text.includes('ogbono') || text.includes('pepper soup') || text.includes('ewedu')) return '🍲';
    if (text.includes('burger') || text.includes('sandwich')) return '🍔';
    if (text.includes('pizza')) return '🍕';
    if (text.includes('shawarma') || text.includes('wrap') || text.includes('roll')) return '🌯';
    if (text.includes('salad') || text.includes('veggie') || text.includes('coleslaw')) return '🥗';
    if (text.includes('pasta') || text.includes('spaghetti') || text.includes('noodles') || text.includes('indomie')) return '🍝';
    if (text.includes('bread') || text.includes('toast') || text.includes('pastry') || text.includes('meat pie') || text.includes('croissant')) return '🥐';
    if (text.includes('cake') || text.includes('donut') || text.includes('muffin') || text.includes('dessert')) return '🍰';
    if (text.includes('plantain') || text.includes('dodo') || text.includes('fries') || text.includes('chips')) return '🍟';
    if (text.includes('yam') || text.includes('potato') || text.includes('porridge')) return '🥔';
    if (text.includes('coffee') || text.includes('latte') || text.includes('espresso') || text.includes('cappuccino')) return '☕';
    if (text.includes('tea') || text.includes('chai') || text.includes('green tea')) return '🍵';
    if (text.includes('juice') || text.includes('smoothie') || text.includes('shake') || text.includes('mocktail')) return '🧃';
    if (text.includes('drink') || text.includes('soda') || text.includes('coke') || text.includes('malt') || text.includes('beverage')) return '🥤';
    if (text.includes('water')) return '💧';
    if (text.includes('cocktail') || text.includes('wine') || text.includes('beer')) return '🍹';
    if (text.includes('ice cream')) return '🍨';
    return '🍽️';
}

function getCategoryIcon(catName: string): string {
    const text = catName.toLowerCase();
    if (text.includes('all')) return '✨';
    if (text.includes('main') || text.includes('rice') || text.includes('meal')) return '🍚';
    if (text.includes('soup') || text.includes('swallow')) return '🍲';
    if (text.includes('protein') || text.includes('meat') || text.includes('grill') || text.includes('bbq') || text.includes('chicken')) return '🍗';
    if (text.includes('fast') || text.includes('snack') || text.includes('finger') || text.includes('side') || text.includes('small chops')) return '🍟';
    if (text.includes('drink') || text.includes('beverage') || text.includes('bar')) return '🥤';
    if (text.includes('dessert') || text.includes('bakery') || text.includes('pastry')) return '🍰';
    if (text.includes('breakfast')) return '🍳';
    return '🍽️';
}

// JWT helpers
function getJwtValue(key: string): string {
    try {
        const token = localStorage.getItem('accessToken');
        if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload[key] || '';
        }
    } catch { }
    return '';
}

function isSuperAdminFromJwt(): boolean {
    try {
        const token = localStorage.getItem('accessToken');
        if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.isSuperAdmin || false;
        }
    } catch { }
    return false;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
function isUuid(val?: string | null): boolean {
    return !!val && UUID_REGEX.test(val);
}

function AccessDenied() {
    const navigate = useNavigate();
    return (
        <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-amber-50">
            <div className="text-center max-w-sm">
                <div className="w-20 h-20 rounded-3xl bg-amber-100 flex items-center justify-center mx-auto mb-6">
                    <Utensils size={40} className="text-amber-500" />
                </div>
                <h2 className="text-xl font-bold text-slate-700 mb-2">Restaurant POS Access Denied</h2>
                <p className="text-sm text-slate-400 mb-6">You don't have access to the Restaurant POS terminal.</p>
                <button onClick={() => navigate('/dashboard')} className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-semibold shadow-lg">Go to Dashboard</button>
            </div>
        </div>
    );
}

function NoShiftScreen({ onOpenShift }: { onOpenShift: () => void }) {
    return (
        <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-amber-50">
            <div className="text-center max-w-sm p-8 bg-white/80 backdrop-blur-md rounded-3xl border border-amber-100 shadow-xl">
                <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-amber-500/30">
                    <ChefHat size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Kitchen Shift Closed</h2>
                <p className="text-sm text-slate-500 mb-6">Open your cashier/kitchen shift to start taking customer orders.</p>
                <button onClick={onOpenShift} className="w-full px-6 py-3.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-bold shadow-lg shadow-amber-500/25 flex items-center justify-center gap-2 hover:opacity-95 transition-all"><DoorOpen size={18} /> Open Shift</button>
            </div>
        </div>
    );
}

function RestaurantMenuItemCard({
    item,
    isAdded,
    onAdd,
    onManagePortions,
}: {
    item: MenuItem;
    isAdded: boolean;
    onAdd: () => void;
    onManagePortions: (item: MenuItem) => void;
}) {
    const yieldNumber = parseFloat(item.yieldQuantity) || 1;
    const availabilityPct = item.totalRemaining > 0 ? Math.min((item.totalRemaining / yieldNumber) * 100, 100) : 0;
    const isLowStock = item.isAvailable && item.totalRemaining <= 5;
    const emoji = getFoodEmoji(item.name, item.categoryName);

    // Determine portion meter color
    const progressGradient = availabilityPct >= 50
        ? 'from-emerald-400 to-teal-500'
        : availabilityPct >= 20
        ? 'from-amber-400 to-orange-500'
        : 'from-rose-400 to-red-500';

    return (
        <motion.div
            whileHover={{ scale: 1.02, y: -3 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => {
                if (item.isAvailable) {
                    onAdd();
                } else {
                    onManagePortions(item);
                }
            }}
            className={clsx(
                'relative rounded-2xl border-2 p-4 text-left transition-all flex flex-col justify-between group select-none min-h-[190px] cursor-pointer',
                item.isAvailable
                    ? 'bg-white border-slate-200/80 hover:border-amber-400 hover:shadow-lg hover:shadow-amber-500/10 active:bg-amber-50/50'
                    : 'bg-slate-50/90 border-dashed border-slate-300 hover:border-amber-400 hover:bg-amber-50/30'
            )}
        >
            {/* Added checkmark animation */}
            <AnimatePresence>
                {isAdded && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.4 }}
                        animate={{ opacity: 1, scale: 1.1 }}
                        exit={{ opacity: 0, scale: 0.4 }}
                        className="absolute -top-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg z-20 text-white"
                    >
                        <CheckCircle2 size={18} />
                    </motion.div>
                )}
            </AnimatePresence>

            <div>
                {/* Top Row: Food Emoji + Urgency Pill / Status */}
                <div className="flex items-start justify-between gap-2 mb-2">
                    <div className={clsx(
                        'w-14 h-14 rounded-2xl flex items-center justify-center text-3xl shadow-sm transition-transform group-hover:scale-105',
                        item.isAvailable ? 'bg-gradient-to-br from-amber-100 via-orange-50 to-amber-200/50 text-slate-800' : 'bg-slate-200/70 text-slate-500'
                    )}>
                        {emoji}
                    </div>

                    <div className="flex flex-col items-end gap-1">
                        {!item.hasActiveProduction ? (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onManagePortions(item);
                                }}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors shadow-xs"
                                title="Click to cook a batch today"
                            >
                                <Plus size={11} className="stroke-[3]" /> Cook Today
                            </button>
                        ) : item.totalRemaining <= 0 ? (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onManagePortions(item);
                                }}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-rose-100 text-rose-800 hover:bg-rose-200 transition-colors shadow-xs"
                                title="Click to add portions"
                            >
                                <Plus size={11} className="stroke-[3]" /> Add Portions
                            </button>
                        ) : isLowStock ? (
                            <div className="flex items-center gap-1">
                                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-amber-100 text-amber-800 border border-amber-300 animate-pulse">
                                    <Zap size={10} className="fill-amber-600 text-amber-600" /> Only {item.totalRemaining} left!
                                </span>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onManagePortions(item);
                                    }}
                                    className="p-1 rounded-full text-slate-400 hover:text-amber-700 hover:bg-amber-100 transition-colors"
                                    title="Add portions"
                                >
                                    <Plus size={12} />
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1">
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    {item.totalRemaining} portions
                                </span>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onManagePortions(item);
                                    }}
                                    className="opacity-0 group-hover:opacity-100 p-1 rounded-full text-slate-400 hover:text-amber-700 hover:bg-amber-100 transition-all"
                                    title="Add portions to batch"
                                >
                                    <Plus size={12} />
                                </button>
                            </div>
                        )}

                        {item.preparationTime && (
                            <span className="flex items-center gap-1 text-[11px] text-slate-400 font-medium">
                                <Timer size={11} /> {item.preparationTime}m
                            </span>
                        )}
                    </div>
                </div>

                {/* Name & Description */}
                <h3 className="text-sm font-bold text-slate-800 group-hover:text-amber-700 transition-colors line-clamp-1">
                    {item.name}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">
                    {item.description || `${item.ingredientCount} fresh ingredients`}
                </p>
            </div>

            {/* Bottom Row: Price & Availability Bar */}
            <div className="mt-3 pt-2 border-t border-slate-100">
                <div className="flex items-baseline justify-between mb-1.5">
                    <p className="text-lg font-black text-amber-600 tracking-tight">
                        ₦{parseFloat(item.retailPrice || '0').toLocaleString()}
                    </p>
                    <span className="text-[11px] font-medium text-slate-400">
                        per {item.yieldUomName || 'portion'}
                    </span>
                </div>

                {item.isAvailable ? (
                    <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                        <div
                            className={clsx('h-1.5 rounded-full bg-gradient-to-r transition-all duration-500', progressGradient)}
                            style={{ width: `${Math.max(availabilityPct, 8)}%` }}
                        />
                    </div>
                ) : (
                    <div className="text-[11px] font-semibold text-amber-700 flex items-center justify-between">
                        <span>Tap to cook or restock</span>
                        <Plus size={13} className="text-amber-600" />
                    </div>
                )}
            </div>
        </motion.div>
    );
}

function QuickPortionModal({
    isOpen,
    onClose,
    tenantId,
    branchId,
    recipes,
    selectedMenuItem,
    activeBatches,
    onSuccess,
}: {
    isOpen: boolean;
    onClose: () => void;
    tenantId: string;
    branchId?: string;
    recipes: BomRecipe[];
    selectedMenuItem: MenuItem | null;
    activeBatches: any[];
    onSuccess: () => void;
}) {
    const addToast = useToastStore((s) => s.addToast);
    const [selectedRecipeId, setSelectedRecipeId] = useState<string>('');
    const [amount, setAmount] = useState<string>('10');
    const [notes, setNotes] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (selectedMenuItem?.recipeId) {
            setSelectedRecipeId(selectedMenuItem.recipeId);
        } else if (recipes && recipes.length > 0 && !selectedRecipeId) {
            setSelectedRecipeId(recipes[0].id);
        }
    }, [selectedMenuItem, recipes]);

    if (!isOpen) return null;

    const currentRecipe = (recipes || []).find(r => r.id === selectedRecipeId);
    const existingActiveBatch = (activeBatches || []).find(
        (b: any) => b.recipeId === selectedRecipeId && b.status === 'active'
    );

    const isTopUpMode = !!existingActiveBatch;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const qty = parseFloat(amount);
        if (isNaN(qty) || qty <= 0) {
            addToast({ type: 'warning', title: 'Invalid Quantity', message: 'Please enter a positive portion count.' });
            return;
        }

        setIsLoading(true);
        try {
            if (isTopUpMode && existingActiveBatch) {
                await addPortions(tenantId, existingActiveBatch.id, qty, branchId);
                addToast({
                    type: 'success',
                    title: 'Portions Added! 🍲',
                    message: `Added ${qty} portions to batch ${existingActiveBatch.batchNumber || ''}. Ready to sell on POS!`
                });
            } else {
                await startProduction(tenantId, {
                    recipeId: selectedRecipeId,
                    quantityProduced: qty,
                    notes: notes || undefined,
                    branchId: branchId || undefined,
                });
                addToast({
                    type: 'success',
                    title: 'Batch Started! 🍳',
                    message: `Prepared ${qty} portions of ${currentRecipe?.name || 'dish'}. Ready on POS!`
                });
            }
            confetti({
                particleCount: 70,
                spread: 60,
                origin: { y: 0.6 }
            });
            onSuccess();
            onClose();
        } catch (err: any) {
            addToast({
                type: 'error',
                title: isTopUpMode ? 'Failed to add portions' : 'Failed to start batch',
                message: err?.response?.data?.error?.message || err?.message || 'Check raw material inventory.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const quickAmounts = isTopUpMode ? [5, 10, 20, 50] : [10, 20, 50, 100];

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 10 }}
                    className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col"
                >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 px-6 py-5 text-white flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-white/20 backdrop-blur-md rounded-2xl shadow-inner">
                                <ChefHat size={22} className="text-white" />
                            </div>
                            <div>
                                <h3 className="text-lg font-black tracking-tight">
                                    {isTopUpMode ? 'Top Up Kitchen Portions' : 'Start Cooking Batch'}
                                </h3>
                                <p className="text-xs text-amber-100 font-medium">
                                    {isTopUpMode
                                        ? 'Increase live portions available for immediate POS orders'
                                        : 'Cook fresh recipe batch & deduct raw material stock'}
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1.5 rounded-full hover:bg-white/20 transition-colors text-white"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 space-y-5">
                        {/* Recipe Selector */}
                        <div>
                            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                                Menu Dish / Recipe
                            </label>
                            <select
                                value={selectedRecipeId}
                                onChange={(e) => setSelectedRecipeId(e.target.value)}
                                className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 font-semibold text-slate-800 text-sm outline-none transition-all"
                            >
                                {(recipes || []).filter(r => r.isActive).map(r => (
                                    <option key={r.id} value={r.id}>
                                        {r.name} ({r.finishedProductName || 'Portions'})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Batch Status Alert */}
                        <div className={clsx(
                            'p-3.5 rounded-2xl border flex items-start gap-3',
                            isTopUpMode
                                ? 'bg-emerald-50/80 border-emerald-200 text-emerald-900'
                                : 'bg-amber-50/80 border-amber-200 text-amber-900'
                        )}>
                            {isTopUpMode ? (
                                <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
                            ) : (
                                <Flame size={18} className="text-amber-600 shrink-0 mt-0.5" />
                            )}
                            <div className="text-xs leading-relaxed">
                                {isTopUpMode && existingActiveBatch ? (
                                    <>
                                        <p className="font-extrabold text-emerald-800">
                                            Active Batch: {existingActiveBatch.batchNumber}
                                        </p>
                                        <p className="text-emerald-700">
                                            Currently {parseFloat(existingActiveBatch.portionsRemaining || '0')} portions remaining. Adding more will update this batch directly.
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p className="font-extrabold text-amber-800">
                                            No active batch cooking today
                                        </p>
                                        <p className="text-amber-700">
                                            Starting this will create Batch 001 for today, allocate inventory ingredients, and make it instantly available on the POS.
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Quantity / Portions to Add */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                                    {isTopUpMode ? 'Portions to Add' : 'Batch Yield Quantity'}
                                </label>
                                <span className="text-[11px] text-slate-400 font-medium">
                                    unit: {currentRecipe?.yieldUomName || 'portion'}
                                </span>
                            </div>

                            <div className="flex items-center gap-2 mb-2">
                                {quickAmounts.map(amt => (
                                    <button
                                        key={amt}
                                        type="button"
                                        onClick={() => setAmount(String(amt))}
                                        className={clsx(
                                            'flex-1 py-2 rounded-xl text-xs font-bold transition-all border',
                                            amount === String(amt)
                                                ? 'bg-amber-500 text-white border-amber-500 shadow-sm shadow-amber-500/20'
                                                : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-amber-50 hover:text-amber-700'
                                        )}
                                    >
                                        +{amt}
                                    </button>
                                ))}
                            </div>

                            <input
                                type="number"
                                min="1"
                                step="any"
                                required
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="Enter quantity"
                                className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-white focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 font-extrabold text-slate-800 text-base outline-none transition-all"
                            />
                        </div>

                        {/* Optional Notes */}
                        {!isTopUpMode && (
                            <div>
                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                                    Production Notes (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="e.g. Lunch rush, special catering, extra spicy"
                                    className="w-full px-4 py-2.5 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 text-xs text-slate-800 outline-none transition-all"
                                />
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-3 pt-2">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={isLoading}
                                className="flex-1 px-4 py-3 rounded-2xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="flex-1 px-5 py-3 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs font-extrabold shadow-lg shadow-amber-500/25 hover:opacity-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {isLoading ? (
                                    <>
                                        <Spinner size="sm" />
                                        <span>Recording in Kitchen...</span>
                                    </>
                                ) : (
                                    <>
                                        <ChefHat size={16} />
                                        <span>
                                            {isTopUpMode
                                                ? `Add +${amount || 0} Portions`
                                                : `Cook ${amount || 0} Portions`}
                                        </span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}

function CategoryTabBar({ groups, selectedId, onSelect }: { groups: CategoryGroup[]; selectedId: string; onSelect: (id: string) => void }) {
    return (
        <div className="flex gap-2 overflow-x-auto py-1 scrollbar-none">
            {groups.map(group => {
                const isSelected = selectedId === group.id;
                const icon = group.icon || getCategoryIcon(group.name);
                return (
                    <button
                        key={group.id}
                        onClick={() => onSelect(group.id)}
                        className={clsx(
                            'flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-bold whitespace-nowrap transition-all shadow-sm',
                            isSelected
                                ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-amber-500/20 scale-[1.02]'
                                : 'bg-white text-slate-600 border border-slate-200 hover:border-amber-300 hover:text-amber-600'
                        )}
                    >
                        <span className="text-base">{icon}</span>
                        <span>{group.name}</span>
                        <span className={clsx(
                            'px-1.5 py-0.5 rounded-full text-[10px]',
                            isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                        )}>
                            {group.items.length}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

export function BomPosPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const pos = usePosStore();
    const addToast = useToastStore((s) => s.addToast);
    const queryClient = useQueryClient();

    const tenantId = user?.tenantId || getJwtValue('tenantId');
    const authSelectedBranchId = useAuthStore((s) => s.selectedBranchId) || getJwtValue('selectedBranchId');

    const [posAccess, setPosAccess] = useState<string>(user?.posAccess || getJwtValue('posAccess') || '');
    const [isCheckingAccess, setIsCheckingAccess] = useState(!user?.posAccess && !getJwtValue('posAccess'));
    const isSuperAdmin = isSuperAdminFromJwt();

    // Set restaurant context for BOM POS
    useEffect(() => { pos.setCartContext('restaurant'); }, []);

    const [showShiftModal, setShowShiftModal] = useState(false);
    const [showShiftCloseModal, setShowShiftCloseModal] = useState(false);
    const [openingCash, setOpeningCash] = useState('');
    const [selectedBranchId, setSelectedBranchId] = useState('');
    const [search, setSearch] = useState('');
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>('all');
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [lastSaleId, setLastSaleId] = useState('');
    const [lastTotal, setLastTotal] = useState(0);

    // Restaurant-specific state
    const [diningMode, setDiningMode] = useState<'dine_in' | 'takeaway' | 'delivery'>('dine_in');
    const [orderNotes, setOrderNotes] = useState('');
    const [tableNumber, setTableNumber] = useState('');
    const [showTableInput, setShowTableInput] = useState(false);
    const [addedItemId, setAddedItemId] = useState<string | null>(null);
    const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
    const [portionModalItem, setPortionModalItem] = useState<MenuItem | null>(null);
    const [isPortionModalOpen, setIsPortionModalOpen] = useState(false);
    const cartEndRef = useRef<HTMLDivElement>(null);

    // Timeout safety
    useEffect(() => {
        if (isProcessing) {
            const timer = setTimeout(() => {
                setIsProcessing(false);
                addToast({ type: 'warning', title: 'Processing Notice', message: 'Order processing took longer than expected. Please verify shift sales.' });
            }, 45000);
            return () => clearTimeout(timer);
        }
    }, [isProcessing, addToast]);

    const { data: shift, isLoading: shiftLoading, refetch: refetchShift } = useQuery({
        queryKey: ['active-shift-bom', tenantId, 'bom'],
        queryFn: () => getActiveShift(tenantId, 'bom'),
        enabled: !!tenantId,
        retry: false,
    });

    const { data: branches } = useQuery({
        queryKey: ['branches-bom-pos', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const effectiveBranchId = selectedBranchId || pos.branchId || authSelectedBranchId || (branches && branches.length > 0 ? branches[0].id : '');

    useEffect(() => {
        if (branches && branches.length > 0 && !selectedBranchId) {
            const main = branches.find((b: any) => b.isMainBranch) || branches[0];
            setSelectedBranchId(main.id);
        }
    }, [branches, selectedBranchId]);

    // Keep posStore in sync with active shift loaded from server
    useEffect(() => {
        if (shift?.id) {
            pos.setShift(shift.id, shift.branchId || effectiveBranchId);
        }
    }, [shift?.id, shift?.branchId, effectiveBranchId]);

    const openShiftMutation = useMutation({
        mutationFn: (cash: string) => openShift(tenantId, selectedBranchId || undefined, cash, 'bom'),
        onSuccess: (data) => {
            pos.setShift(data.id, data.branchId || effectiveBranchId);
            setShowShiftModal(false);
            setOpeningCash('');
            addToast({ type: 'success', title: 'Shift Opened ✅', message: 'Kitchen register ready for orders.' });
            refetchShift();
            queryClient.invalidateQueries({ queryKey: ['production-today-pos'] });
        },
        onError: async (err: any) => {
            const errMsg = err?.response?.data?.message || err?.message || '';
            const errCode = err?.response?.data?.error?.code || err?.code || '';
            const isAlreadyOpen =
                errCode === 'SHIFT_ALREADY_OPEN' ||
                errMsg.toLowerCase().includes('already have an open') ||
                errMsg.toLowerCase().includes('shift_already_open');

            if (isAlreadyOpen) {
                try {
                    const active = await getActiveShift(tenantId, 'bom');
                    if (active && active.status === 'open') {
                        pos.setShift(active.id, active.branchId || effectiveBranchId);
                        setShowShiftModal(false);
                        refetchShift();
                        addToast({
                            type: 'info',
                            title: 'Active Shift Found',
                            message: 'Resumed your active Kitchen POS register shift.',
                        });
                        return;
                    }
                } catch (e) {
                    console.error('Failed to auto-resume active BOM shift:', e);
                }
            }
            addToast({ type: 'error', title: 'Failed to open shift', message: errMsg || 'Unable to open shift' });
        },
    });

    const { data: recipes, isLoading: recipesLoading } = useQuery({
        queryKey: ['bom-recipes-menu', tenantId],
        queryFn: () => getAllRecipes(tenantId),
        enabled: !!tenantId,
    });

    const { data: todayBatches, refetch: refetchBatches } = useQuery({
        queryKey: ['production-today-pos', tenantId],
        queryFn: () => getTodayBatches(tenantId),
        enabled: !!tenantId,
        refetchInterval: 10000,
        refetchOnWindowFocus: true,
    });

    const menuItems = useMemo((): MenuItem[] => {
        if (!recipes) return [];
        const activeRecipes = recipes.filter((r: any) => r.isActive);
        const allActiveBatches = (todayBatches || []).filter((b: any) => b.status === 'active');

        return activeRecipes.map((recipe: any) => {
            const recipeBatches = allActiveBatches
                .filter((b: any) => b.recipeId === recipe.id)
                .map((b: any) => ({
                    batchId: b.id,
                    batchNumber: b.batchNumber,
                    portionsRemaining: parseFloat(b.portionsRemaining),
                    sellingPrice: b.sellingPrice || '0',
                }));

            const totalRemaining = recipeBatches.reduce((sum: number, b: { portionsRemaining: number }) => sum + b.portionsRemaining, 0);
            const hasActiveProduction = recipeBatches.length > 0;
            const isAvailable = hasActiveProduction && totalRemaining > 0;

            let sellingPrice = '0';
            if (recipeBatches.length > 0 && parseFloat(recipeBatches[0].sellingPrice) > 0) {
                sellingPrice = recipeBatches[0].sellingPrice;
            } else if (recipe.finishedProductRetailPrice && parseFloat(recipe.finishedProductRetailPrice) > 0) {
                sellingPrice = recipe.finishedProductRetailPrice;
            }

            const catId = recipe.finishedProductCategoryId || recipe.categoryId || 'menu';
            const catName = recipe.categoryName || 'Menu';

            return {
                recipeId: recipe.id,
                productId: recipe.finishedProductId,
                name: recipe.name,
                categoryId: catId,
                categoryName: catName,
                description: recipe.description,
                retailPrice: sellingPrice,
                yieldQuantity: recipe.yieldQuantity,
                yieldUomId: recipe.yieldUomId,
                yieldUomName: recipe.yieldUomName || 'serving',
                preparationTime: recipe.preparationTime,
                ingredientCount: recipe.items?.length || 0,
                isAvailable,
                totalRemaining,
                hasActiveProduction,
                activeBatches: recipeBatches,
            };
        });
    }, [recipes, todayBatches]);

    const categorizedMenu = useMemo((): CategoryGroup[] => {
        const groups = new Map<string, CategoryGroup>();
        groups.set('all', { id: 'all', name: 'All Items', icon: '✨', items: [] });
        menuItems.forEach(item => {
            const catId = item.categoryId || 'uncategorized';
            const catName = item.categoryName || 'Menu';
            if (!groups.has(catId)) {
                groups.set(catId, { id: catId, name: catName, icon: getCategoryIcon(catName), items: [] });
            }
            groups.get(catId)!.items.push(item);
            groups.get('all')!.items.push(item);
        });
        return Array.from(groups.values());
    }, [menuItems]);

    const displayedGroups = useMemo(() => {
        let groups = selectedCategoryId === 'all'
            ? categorizedMenu.filter(g => g.id !== 'all')
            : categorizedMenu.filter(g => g.id === selectedCategoryId);

        return groups.map(group => {
            let items = group.items;
            if (search) {
                const q = search.toLowerCase();
                items = items.filter(item => item.name.toLowerCase().includes(q) || (item.description || '').toLowerCase().includes(q));
            }
            if (showOnlyAvailable) items = items.filter(item => item.isAvailable);
            return { ...group, items };
        }).filter(group => group.items.length > 0);
    }, [categorizedMenu, selectedCategoryId, search, showOnlyAvailable]);

    const handleAddToCart = useCallback((item: MenuItem) => {
        if (!item.isAvailable) {
            addToast({ type: 'warning', title: 'Unavailable', message: `${item.name} is not available right now.` });
            return;
        }
        const cartItemsForProduct = pos.cart.filter(i => i.productId === item.productId);
        const currentQtyInCart = cartItemsForProduct.reduce((sum, i) => sum + i.quantity, 0);
        if (currentQtyInCart + 1 > item.totalRemaining) {
            addToast({ type: 'error', title: 'Limited Stock', message: `Only ${item.totalRemaining} portion(s) available.` });
            return;
        }

        let price = parseFloat(item.retailPrice) || 0;
        if (price <= 0) {
            price = 1000;
            addToast({ type: 'warning', title: 'Price Not Set', message: `Using default ₦${price.toLocaleString()} for ${item.name}.` });
        }

        const existingItem = pos.cart.find(i => i.productId === item.productId);
        if (existingItem) {
            pos.updateQuantity(existingItem.id, existingItem.quantity + 1);
        } else {
            pos.addItem({
                id: '',
                productId: item.productId,
                productName: item.name,
                variantId: undefined,
                variantName: undefined,
                uomId: item.yieldUomId,
                uomName: item.yieldUomName || 'serving',
                uomAbbreviation: item.yieldUomName || 'serv',
                quantity: 1,
                unitPrice: price,
                discountAmount: 0,
                taxAmount: 0,
                totalAmount: price,
                imageUrl: undefined,
            });
        }
        setAddedItemId(item.productId);
        setTimeout(() => setAddedItemId(null), 600);
        setTimeout(() => { cartEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, 100);
    }, [pos, addToast]);

    const handleCustomerAttach = useCallback(async (phone: string) => {
        try {
            const customer = await findCustomerByPhone(tenantId, phone);
            if (customer) {
                const c = customer as any;
                const tier = (c.loyaltyTier || 'bronze') as 'bronze' | 'silver' | 'gold' | 'platinum';
                pos.setCustomer(c.id, `${c.firstName} ${c.lastName}`, {
                    id: c.id,
                    name: `${c.firstName} ${c.lastName}`,
                    phone: c.phone || '',
                    loyaltyCardNumber: c.loyaltyCardNumber || '',
                    loyaltyPoints: parseFloat(c.loyaltyPoints || '0'),
                    loyaltyTier: tier,
                    storeCredit: parseFloat(c.storeCredit || '0'),
                    tierDiscountPercent: 0
                });
                addToast({ type: 'success', title: 'Customer Attached', message: `Welcome ${c.firstName}!` });
                return customer;
            }
            addToast({ type: 'warning', title: 'Not Found', message: 'No customer found with that phone.' });
            return null;
        } catch {
            addToast({ type: 'error', title: 'Error', message: 'Could not search customer.' });
            return null;
        }
    }, [tenantId, pos, addToast]);

    const handleQuickCreateCustomer = useCallback(async (form: { firstName: string; lastName: string; phone: string; dateOfBirth?: string }) => {
        try {
            const { data } = await apiClient.post(`/tenants/${tenantId}/customers`, form);
            const c = (data?.data || data) as any;
            const tier = (c.loyaltyTier || 'bronze') as 'bronze' | 'silver' | 'gold' | 'platinum';
            pos.setCustomer(c.id, `${c.firstName} ${c.lastName}`, {
                id: c.id,
                name: `${c.firstName} ${c.lastName}`,
                phone: c.phone || '',
                loyaltyCardNumber: c.loyaltyCardNumber || '',
                loyaltyPoints: parseFloat(c.loyaltyPoints || '0'),
                loyaltyTier: tier,
                storeCredit: parseFloat(c.storeCredit || '0'),
                tierDiscountPercent: 0
            });
            addToast({ type: 'success', title: 'Customer Created', message: `${c.firstName} registered.` });
            return c;
        } catch {
            addToast({ type: 'error', title: 'Failed', message: 'Could not create customer profile.' });
            return null;
        }
    }, [tenantId, pos, addToast]);

    const handlePaymentComplete = useCallback(async (payments: PaymentItem[], discountTotal: string) => {
        const currentShiftId = shift?.id || pos.activeShiftId;
        if (!currentShiftId) {
            addToast({ type: 'error', title: 'No active shift', message: 'Please open a shift before taking orders.' });
            return;
        }
        if (pos.cart.length === 0) return;

        setIsProcessing(true);
        try {
            const modePrefix = diningMode === 'takeaway' ? '[TAKEAWAY] ' : diningMode === 'delivery' ? '[DELIVERY] ' : tableNumber ? `[Table ${tableNumber}] ` : '';
            const combinedNotes = `${modePrefix}${orderNotes}`.trim();

            // Resolve robust, validated branch ID
            const cleanBranchId = isUuid(selectedBranchId)
                ? selectedBranchId
                : isUuid(pos.branchId)
                ? pos.branchId
                : isUuid(authSelectedBranchId)
                ? authSelectedBranchId
                : (branches && branches.length > 0 && isUuid(branches[0].id) ? branches[0].id : undefined);

            if (!cleanBranchId) {
                addToast({ type: 'error', title: 'Branch Required', message: 'Please select an active branch in shift or settings before concluding sales.' });
                setIsProcessing(false);
                return;
            }

            // Clean items and ensure valid UOM IDs
            const cleanedItems = pos.cart.map(item => {
                const menuItem = menuItems.find(mi => mi.productId === item.productId);
                const effectiveUomId = (item.uomId && item.uomId !== item.productId && isUuid(item.uomId))
                    ? item.uomId
                    : (menuItem?.yieldUomId && isUuid(menuItem.yieldUomId))
                    ? menuItem.yieldUomId
                    : (isUuid(item.uomId) ? item.uomId : undefined);

                return {
                    productId: item.productId,
                    uomId: effectiveUomId || (isUuid(item.uomId) ? item.uomId : undefined),
                    quantity: (item.quantity || 1).toString(),
                    unitPrice: (item.unitPrice || 0).toString(),
                    discountAmount: (item.discountAmount || 0).toString(),
                };
            });

            // Clean tableId: ONLY pass tableId if it is a valid UUID!
            // Quick labels like "T1", "Bar", "Patio" are already saved in combinedNotes.
            const validTableId = isUuid(tableNumber) ? tableNumber : undefined;

            const sale = await directCheckoutWithTimeout(tenantId, {
                branchId: cleanBranchId,
                shiftId: currentShiftId,
                items: cleanedItems,
                payments,
                customerId: isUuid(pos.customerId) ? (pos.customerId as string) : undefined,
                saleType: 'retail',
                tableId: validTableId,
                notes: combinedNotes || undefined,
                discountTotal: (discountTotal || '0').toString(),
            }, 30000);

            // Trigger celebration confetti
            confetti({
                particleCount: 90,
                spread: 70,
                origin: { y: 0.6 }
            });

            setLastSaleId(sale.id);
            setLastTotal(pos.grandTotal);
            pos.clearCart();
            setOrderNotes('');
            setTableNumber('');
            pos.setPaymentModalOpen(false); // Close payment modal on SUCCESS
            refetchShift();
            queryClient.invalidateQueries({ queryKey: ['production-today-pos'] });
            setShowReceiptModal(true);
            addToast({ type: 'success', title: 'Order Completed! 🍽️', message: 'Sale recorded and kitchen batches updated.' });
        } catch (err: any) {
            addToast({ type: 'error', title: 'Checkout Failed', message: err?.response?.data?.error?.message || err?.message || 'Could not complete order.' });
        } finally {
            setIsProcessing(false);
        }
    }, [tenantId, pos, shift, authSelectedBranchId, selectedBranchId, branches, diningMode, tableNumber, orderNotes, addToast, refetchShift, menuItems, queryClient]);

    const maxQuantitiesForCart = useMemo(() => {
        const map: Record<string, number> = {};
        menuItems.forEach(item => { map[item.productId] = item.totalRemaining; });
        return map;
    }, [menuItems]);

    // Quick cash shortcuts
    const quickCashOptions = useMemo(() => {
        const total = pos.grandTotal;
        if (total <= 0) return [];
        const options: number[] = [total];
        const next500 = Math.ceil(total / 500) * 500;
        const next1000 = Math.ceil(total / 1000) * 1000;
        const next5000 = Math.ceil(total / 5000) * 5000;

        if (next500 > total && !options.includes(next500)) options.push(next500);
        if (next1000 > total && !options.includes(next1000)) options.push(next1000);
        if (next5000 > total && !options.includes(next5000)) options.push(next5000);

        return options;
    }, [pos.grandTotal]);

    if (isCheckingAccess) return <div className="h-screen flex items-center justify-center bg-slate-50"><Spinner size="xl" /></div>;
    if (!isSuperAdmin && posAccess !== 'bom' && posAccess !== 'both') return <AccessDenied />;
    if (shiftLoading) return <div className="h-screen flex items-center justify-center bg-slate-50"><Spinner size="xl" /></div>;
    if (!shift) return (
        <>
            <NoShiftScreen onOpenShift={() => setShowShiftModal(true)} />
            <ShiftModal
                isOpen={showShiftModal}
                onClose={() => setShowShiftModal(false)}
                onConfirm={(cash) => openShiftMutation.mutate(cash)}
                isLoading={openShiftMutation.isPending}
                openingCash={openingCash}
                onOpeningCashChange={setOpeningCash}
                branches={branches || []}
                selectedBranchId={selectedBranchId}
                onBranchChange={setSelectedBranchId}
            />
        </>
    );

    return (
        <div className="h-screen flex flex-col bg-slate-100 overflow-hidden font-sans">
            {/* Top Navigation Bar */}
            <header className="bg-white/95 backdrop-blur-md border-b border-slate-200 px-5 py-3 flex-shrink-0 shadow-sm z-20">
                <div className="flex items-center justify-between gap-4">
                    {/* Left: Branding & Kitchen Counter */}
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate('/dashboard')} className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 transition-colors">
                            <ArrowLeft size={18} />
                        </button>
                        <div className="p-2.5 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-md shadow-amber-500/20 text-white">
                            <Utensils size={20} />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-base font-extrabold text-slate-900 tracking-tight">Restaurant POS</h1>
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
                                    Fast-Casual Mode
                                </span>
                            </div>
                            <p className="text-xs text-slate-500 flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                {menuItems.filter(i => i.isAvailable).length} ready to serve · {todayBatches?.filter((b: any) => b.status === 'active').length || 0} batches cooking
                            </p>
                        </div>
                    </div>

                    {/* Middle: Dining Mode Switcher */}
                    <div className="flex items-center bg-slate-100/80 p-1 rounded-2xl border border-slate-200/60 shadow-inner">
                        <button
                            onClick={() => setDiningMode('dine_in')}
                            className={clsx(
                                'flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-bold transition-all',
                                diningMode === 'dine_in'
                                    ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-md shadow-amber-500/25 scale-[1.02]'
                                    : 'text-slate-600 hover:text-slate-900'
                            )}
                        >
                            <Utensils size={14} /> Dine-In
                        </button>
                        <button
                            onClick={() => setDiningMode('takeaway')}
                            className={clsx(
                                'flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-bold transition-all',
                                diningMode === 'takeaway'
                                    ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-md shadow-amber-500/25 scale-[1.02]'
                                    : 'text-slate-600 hover:text-slate-900'
                            )}
                        >
                            <ShoppingBag size={14} /> Takeaway
                        </button>
                        <button
                            onClick={() => setDiningMode('delivery')}
                            className={clsx(
                                'flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-bold transition-all',
                                diningMode === 'delivery'
                                    ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-md shadow-amber-500/25 scale-[1.02]'
                                    : 'text-slate-600 hover:text-slate-900'
                            )}
                        >
                            <Bike size={14} /> Delivery
                        </button>
                    </div>

                    {/* Right: Kitchen Quick-link, Close Shift & Customer */}
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                setPortionModalItem(null);
                                setIsPortionModalOpen(true);
                            }}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:opacity-95 transition-all shadow-sm shadow-amber-500/20 active:scale-95"
                            title="Top up portions or start cooking a recipe batch"
                        >
                            <Plus size={15} /> Portions
                        </button>
                        <button
                            onClick={() => navigate('/bom/production')}
                            className="hidden lg:flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
                            title="Manage today's kitchen batches"
                        >
                            <ChefHat size={15} /> Batches
                        </button>
                        <button
                            onClick={() => setShowCustomerModal(true)}
                            className={clsx(
                                'flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold transition-all shadow-sm',
                                pos.customerName
                                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                    : 'bg-white text-slate-700 border border-slate-200 hover:border-amber-300'
                            )}
                        >
                            <Users size={15} />
                            <span className="max-w-[100px] truncate">{pos.customerName || 'Walk-in Guest'}</span>
                        </button>
                        <button
                            onClick={() => setShowShiftCloseModal(true)}
                            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition-colors"
                        >
                            <DoorOpen size={15} /> Close Shift
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content Area */}
            <div className="flex-1 flex overflow-hidden">
                {/* Menu Catalog Panel */}
                <div className="flex-1 flex flex-col overflow-hidden bg-slate-50/50">
                    {/* Search & Category Filter */}
                    <div className="px-5 py-3 bg-white border-b border-slate-200/80 flex-shrink-0 space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="relative flex-1">
                                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search dishes, drinks, or ingredients..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="w-full pl-10 pr-10 py-2.5 border border-slate-200 rounded-2xl text-sm bg-slate-50 focus:bg-white focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 transition-all outline-none"
                                />
                                {search && (
                                    <button onClick={() => setSearch('')} className="absolute right-3.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600">
                                        <X size={14} />
                                    </button>
                                )}
                            </div>

                            <button
                                onClick={() => refetchBatches()}
                                className="p-2.5 rounded-2xl border border-slate-200 bg-white text-slate-500 hover:text-amber-600 hover:border-amber-300 transition-colors"
                                title="Refresh live kitchen availability"
                            >
                                <RefreshCw size={16} />
                            </button>

                            <button
                                onClick={() => setShowOnlyAvailable(!showOnlyAvailable)}
                                className={clsx(
                                    'flex items-center gap-1.5 px-3.5 py-2.5 rounded-2xl border text-xs font-bold transition-all',
                                    showOnlyAvailable
                                        ? 'bg-emerald-50 border-emerald-300 text-emerald-700 shadow-sm'
                                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                                )}
                            >
                                <SlidersHorizontal size={14} />
                                <span className="hidden sm:inline">Ready Now</span>
                            </button>
                        </div>

                        <CategoryTabBar
                            groups={categorizedMenu}
                            selectedId={selectedCategoryId}
                            onSelect={setSelectedCategoryId}
                        />
                    </div>

                    {/* Menu Item Cards Grid */}
                    <div className="flex-1 overflow-y-auto p-5">
                        {recipesLoading ? (
                            <div className="flex flex-col items-center justify-center h-full text-slate-400">
                                <Spinner size="xl" />
                                <p className="mt-3 text-sm font-medium">Loading delicious menu...</p>
                            </div>
                        ) : displayedGroups.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-slate-400 py-16">
                                <div className="w-16 h-16 rounded-3xl bg-amber-50 flex items-center justify-center mb-3 text-amber-500">
                                    <Coffee size={36} />
                                </div>
                                <h3 className="text-base font-bold text-slate-700">No dishes match your filter</h3>
                                <p className="text-xs text-slate-400 mt-1 max-w-sm text-center">
                                    {showOnlyAvailable ? 'No items are in active production right now. Start a batch in Daily Production.' : 'Try adjusting your search keyword.'}
                                </p>
                                {showOnlyAvailable && (
                                    <button
                                        onClick={() => setShowOnlyAvailable(false)}
                                        className="mt-4 px-4 py-2 bg-amber-500 text-white rounded-xl text-xs font-bold hover:bg-amber-600 transition-colors shadow-sm"
                                    >
                                        Show All Menu Items
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {displayedGroups.map(group => (
                                    <div key={group.id} className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-xs font-extrabold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                                                <span>{group.icon || '🍽️'}</span> {group.name}
                                            </h3>
                                            <span className="text-[11px] font-medium text-slate-400">
                                                {group.items.length} {group.items.length === 1 ? 'item' : 'items'}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3.5">
                                            {group.items.map(item => (
                                                <RestaurantMenuItemCard
                                                    key={item.productId}
                                                    item={item}
                                                    isAdded={addedItemId === item.productId}
                                                    onAdd={() => handleAddToCart(item)}
                                                    onManagePortions={(it) => {
                                                        setPortionModalItem(it);
                                                        setIsPortionModalOpen(true);
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Side: Fast-Casual Order Panel */}
                <aside className="w-[420px] flex-shrink-0 shadow-2xl z-10 border-l border-slate-200 bg-white flex flex-col">
                    {/* Restaurant Meta: Table & Notes */}
                    <div className="bg-slate-50/70 border-b border-slate-200 px-4 py-3 flex-shrink-0 space-y-2.5">
                        {/* Table Selector (Dine-In only) */}
                        {diningMode === 'dine_in' && (
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between text-xs text-slate-600 font-bold">
                                    <span className="flex items-center gap-1.5">
                                        <Table2 size={13} className="text-amber-600" /> Table Selection:
                                    </span>
                                    {tableNumber && (
                                        <button onClick={() => setTableNumber('')} className="text-[11px] text-rose-500 hover:underline">
                                            Clear ({tableNumber})
                                        </button>
                                    )}
                                </div>
                                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                                    {['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'Bar', 'Patio'].map(tbl => (
                                        <button
                                            key={tbl}
                                            onClick={() => setTableNumber(tableNumber === tbl ? '' : tbl)}
                                            className={clsx(
                                                'px-2.5 py-1 rounded-xl text-xs font-bold transition-all shrink-0',
                                                tableNumber === tbl
                                                    ? 'bg-amber-500 text-white shadow-sm shadow-amber-500/30'
                                                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-amber-50 hover:text-amber-700'
                                            )}
                                        >
                                            {tbl}
                                        </button>
                                    ))}
                                    <button
                                        onClick={() => setShowTableInput(!showTableInput)}
                                        className={clsx(
                                            'px-2.5 py-1 rounded-xl text-xs font-semibold shrink-0 border border-dashed transition-colors',
                                            showTableInput ? 'border-amber-400 text-amber-600 bg-amber-50' : 'border-slate-300 text-slate-500 hover:border-slate-400'
                                        )}
                                    >
                                        + Other
                                    </button>
                                </div>
                                {showTableInput && (
                                    <div className="flex gap-2 pt-1">
                                        <input
                                            type="text"
                                            placeholder="Enter table or seat name..."
                                            value={tableNumber}
                                            onChange={(e) => setTableNumber(e.target.value)}
                                            className="flex-1 px-3 py-1.5 border border-slate-200 rounded-xl text-xs bg-white focus:ring-2 focus:ring-amber-500/20 outline-none"
                                            autoFocus
                                        />
                                        <button onClick={() => setShowTableInput(false)} className="px-3 py-1.5 bg-slate-200 rounded-xl text-xs font-bold text-slate-700">
                                            Done
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Order Notes & Quick Kitchen Instructions */}
                        <div className="space-y-1.5">
                            <div className="relative">
                                <MessageSquare className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Kitchen notes: e.g. Less pepper, pack soup separately..."
                                    value={orderNotes}
                                    onChange={(e) => setOrderNotes(e.target.value)}
                                    className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-xl text-xs bg-white focus:ring-2 focus:ring-amber-500/20 outline-none"
                                />
                            </div>
                            <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 scrollbar-none">
                                {['No Pepper', 'Extra Spicy', 'Pack Separate', 'Urgent Order', 'Extra Cutlery'].map(chip => (
                                    <button
                                        key={chip}
                                        onClick={() => setOrderNotes(prev => prev ? `${prev}, ${chip}` : chip)}
                                        className="px-2 py-0.5 rounded-lg text-[10px] font-semibold bg-white border border-slate-200 text-slate-600 hover:border-amber-300 hover:text-amber-700 transition-colors shrink-0"
                                    >
                                        +{chip}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Shared Cart Panel (Safe with portion limits) */}
                    <div className="flex-1 overflow-hidden flex flex-col">
                        <CartPanel
                            onCheckout={() => pos.setPaymentModalOpen(true)}
                            disabled={!pos.activeShiftId || isProcessing}
                            maxQuantities={maxQuantitiesForCart}
                        />
                    </div>

                    {/* Quick Tender Fast Cash Bar */}
                    {pos.cart.length > 0 && quickCashOptions.length > 0 && (
                        <div className="bg-amber-50/70 border-t border-amber-100 px-4 py-2 flex items-center justify-between gap-2">
                            <span className="text-[11px] font-bold text-amber-800 flex items-center gap-1">
                                <Banknote size={13} className="text-amber-600" /> Fast Tender:
                            </span>
                            <div className="flex items-center gap-1.5 overflow-x-auto">
                                {quickCashOptions.map((amount, idx) => (
                                    <button
                                        key={amount}
                                        onClick={() => pos.setPaymentModalOpen(true)}
                                        className="px-2 py-1 rounded-lg text-xs font-bold bg-white text-amber-900 border border-amber-200 hover:bg-amber-100 hover:border-amber-400 transition-all shadow-xs shrink-0"
                                    >
                                        {idx === 0 ? 'Exact ₦' + amount.toLocaleString() : '₦' + amount.toLocaleString()}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div ref={cartEndRef} />
                </aside>
            </div>

            {/* Modals */}
            <ShiftModal
                isOpen={showShiftModal}
                onClose={() => setShowShiftModal(false)}
                onConfirm={(cash) => openShiftMutation.mutate(cash)}
                isLoading={openShiftMutation.isPending}
                openingCash={openingCash}
                onOpeningCashChange={setOpeningCash}
                branches={branches || []}
                selectedBranchId={selectedBranchId}
                onBranchChange={setSelectedBranchId}
            />

            <ShiftCloseModal
                isOpen={showShiftCloseModal}
                onClose={() => setShowShiftCloseModal(false)}
                shift={shift}
                onShiftClosed={() => {
                    pos.setShift(null, '');
                    pos.clearCart();
                    setOrderNotes('');
                    setTableNumber('');
                    setShowShiftCloseModal(false);
                    refetchShift();
                    queryClient.invalidateQueries({ queryKey: ['production-today-pos'] });
                }}
            />

            <CustomerModal
                isOpen={showCustomerModal}
                onClose={() => setShowCustomerModal(false)}
                onSearch={handleCustomerAttach}
                onCreateCustomer={handleQuickCreateCustomer}
                currentCustomer={pos.customerInfo}
                onRemoveCustomer={() => pos.setCustomer(null, null)}
            />

            <PaymentModal
                isOpen={pos.isPaymentModalOpen}
                onClose={() => pos.setPaymentModalOpen(false)}
                onComplete={async (payments, discountTotal) => {
                    await handlePaymentComplete(payments, discountTotal);
                }}
                isLoading={isProcessing}
                branchId={effectiveBranchId}
            />

            <ReceiptModal
                isOpen={showReceiptModal}
                onClose={() => setShowReceiptModal(false)}
                onNewSale={() => {
                    setShowReceiptModal(false);
                    pos.clearCart();
                    setOrderNotes('');
                    setTableNumber('');
                }}
                tenantId={tenantId}
                saleId={lastSaleId}
                total={lastTotal}
            />

            <QuickPortionModal
                isOpen={isPortionModalOpen}
                onClose={() => {
                    setIsPortionModalOpen(false);
                    setPortionModalItem(null);
                }}
                tenantId={tenantId}
                branchId={effectiveBranchId}
                recipes={recipes || []}
                selectedMenuItem={portionModalItem}
                activeBatches={todayBatches || []}
                onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: ['production-today-pos'] });
                    queryClient.invalidateQueries({ queryKey: ['production-today'] });
                    queryClient.invalidateQueries({ queryKey: ['bom-recipes-menu'] });
                    refetchBatches();
                }}
            />
        </div>
    );
}